
import { useState } from "react";
import { ChevronRight, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/use-language";

interface PrivacyConsentProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}

/**
 * Privacy-consent block per Korean PIPA Art. 15(2): purpose / items / retention / refusal right.
 * Uses a checkbox with an inline "자세히 보기" dialog so users can read the full notice
 * before consenting.
 */
export function PrivacyConsent({
  checked,
  onCheckedChange,
  disabled,
}: PrivacyConsentProps) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex items-start gap-2.5">
        <input
          id="privacy-consent-checkbox"
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onCheckedChange(e.target.checked)}
          className="mt-0.5 size-4 cursor-pointer rounded border-border accent-primary"
          aria-describedby="privacy-summary"
        />
        <label
          htmlFor="privacy-consent-checkbox"
          className="flex-1 cursor-pointer text-sm"
        >
          <p className="font-medium">
            <span className="text-destructive">*</span>{" "}
            {t("privacy.consent_label")}
          </p>
          <p
            id="privacy-summary"
            className="mt-0.5 text-xs text-muted-foreground"
          >
            {t("privacy.consent_summary")}
          </p>
        </label>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger
            render={
              <button
                type="button"
                className="mt-0.5 inline-flex shrink-0 cursor-pointer items-center gap-0.5 text-xs font-medium text-primary hover:underline"
              >
                {t("privacy.view_details")}
                <ChevronRight className="size-3" />
              </button>
            }
          />
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="size-5 text-primary" />
                {t("privacy.modal_title")}
              </DialogTitle>
              <DialogDescription>
                {t("privacy.modal_desc")}
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="border-b bg-muted/50 text-left">
                    <th className="w-24 px-3 py-2 font-semibold">
                      {t("privacy.col_item")}
                    </th>
                    <th className="px-3 py-2 font-semibold">
                      {t("privacy.col_detail")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="px-3 py-2 align-top font-medium">
                      {t("privacy.row_purpose")}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {t("privacy.val_purpose")}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-3 py-2 align-top font-medium">
                      {t("privacy.row_items")}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {t("privacy.val_items")}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-3 py-2 align-top font-medium">
                      {t("privacy.row_retention")}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {t("privacy.val_retention")}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 align-top font-medium">
                      {t("privacy.row_refusal")}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {t("privacy.val_refusal")}
                    </td>
                  </tr>
                </tbody>
              </table>

              <p className="mt-4 rounded-md bg-muted/60 p-3 text-[11px] leading-relaxed text-muted-foreground">
                {t("privacy.legal_notice")}
              </p>
            </div>

            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                onClick={() => setOpen(false)}
              >
                {t("privacy.close")}
              </Button>
              <Button
                size="sm"
                className="cursor-pointer"
                onClick={() => {
                  onCheckedChange(true);
                  setOpen(false);
                }}
              >
                {t("privacy.agree_and_close")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
